
import { Store, Star, Package, CheckCheck, ThumbsUp } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { Vendor } from "@/types/shop";

interface VendorCardProps {
  vendor: Vendor;
  viewMode: "grid" | "list";
}

const VendorCard = ({ vendor, viewMode }: VendorCardProps) => {
  const navigate = useNavigate();

  // Random metrics for display (in a real app these would come from the API)
  const fulfillmentRate = vendor.fulfillment_rate || Math.floor(Math.random() * 20) + 80; // 80-100%
  const satisfactionScore = vendor.satisfaction_score || (Math.floor(Math.random() * 10) + 40) / 10; // 4.0-5.0

  const handleVendorClick = () => {
    navigate(`/shop/${vendor.id}`);
  };

  return (
    <Card 
      className={`overflow-hidden transition-all duration-200 hover:shadow-lg border-l-4 border-l-[#9b87f5] ${
        viewMode === "list" ? "flex" : ""
      }`}
    >
      <div className={`${
        viewMode === "list" 
          ? "flex-shrink-0 w-48" 
          : "w-full aspect-video"
      } relative group cursor-pointer`}
      onClick={handleVendorClick}>
        <div className="w-full h-full bg-gradient-to-br from-purple-100 to-purple-50 flex items-center justify-center">
          <Store className="h-12 w-12 text-[#9b87f5]" />
        </div>
      </div>
      <div className="p-6 flex flex-col h-full">
        <div className="flex-1">
          <div className="flex justify-between items-start mb-2">
            <h2 className="text-xl font-semibold text-primary cursor-pointer hover:text-[#9b87f5]" onClick={handleVendorClick}>
              {vendor.name}
            </h2>
            <div className="flex items-center gap-1">
              <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
              <span className="text-sm font-medium">{vendor.rating}</span>
            </div>
          </div>
          <p className="text-sm text-muted-foreground mb-2">
            {vendor.business_type}
          </p>
          
          {/* Performance Metrics */}
          <div className="space-y-2 mb-4">
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center text-muted-foreground">
                <Package className="h-3 w-3 mr-1" />
                Total Sales:
              </span>
              <span className="font-medium">{vendor.total_sales}</span>
            </div>
            
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center text-muted-foreground">
                <CheckCheck className="h-3 w-3 mr-1" />
                Fulfillment Rate:
              </span>
              <span className="font-medium text-green-600">{fulfillmentRate}%</span>
            </div>
            
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center text-muted-foreground">
                <ThumbsUp className="h-3 w-3 mr-1" />
                Satisfaction:
              </span>
              <span className="font-medium text-green-600">{satisfactionScore}/5</span>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Button 
            className="w-full bg-[#9b87f5] hover:bg-[#7E69AB]" 
            size="lg"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/shop/${vendor.id}`);
            }}
          >
            Visit Store
          </Button>
          <Button 
            variant="outline" 
            className="w-full border-[#9b87f5] text-[#9b87f5] hover:bg-[#9b87f5] hover:text-white" 
            size="lg"
            onClick={(e) => e.stopPropagation()}
          >
            Learn More
          </Button>
        </div>
      </div>
    </Card>
  );
};

export default VendorCard;
